/**
 * A small labeled list in the sidebar (Favorites, Shared with me): an
 * uppercase label over one-line rows. Each row opens its item; a row may
 * carry trailing controls (Favorites' reorder arrows and remove star) and,
 * opted in, drag and drop handlers with a drop line (Favorites' reorder), the
 * same insertion line as the folder tree's (`TreeDropLine`).
 */

import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { useId, type DragEventHandler, type ReactNode } from 'react';

import type { GlyphRef } from '../../../theme/glyph';
import { Glyph } from '../../ui/Glyph';
import { TreeDropLine } from './TreeDropLine';

interface SidebarSectionProps {
  readonly label: string;
  readonly testId: string;
  readonly children: ReactNode;
}

export function SidebarSection({ label, testId, children }: SidebarSectionProps) {
  const labelId = useId();
  return (
    <Box as="section" data-testid={testId} flexShrink={0} maxH="30%" overflowY="auto" px={2}>
      <Text
        id={labelId}
        px={2}
        pt={3}
        pb={1}
        fontSize="12px"
        fontWeight="medium"
        letterSpacing="0.08em"
        textTransform="uppercase"
        color="text.muted"
      >
        {label}
      </Text>
      <Box as="ul" role="list" listStyleType="none" aria-labelledby={labelId}>
        {children}
      </Box>
    </Box>
  );
}

interface SidebarRowProps {
  readonly label: string;
  readonly glyph: GlyphRef;
  readonly onOpen: () => void;
  readonly trailing?: ReactNode;
  /** Opt-in: the row is a drag source and drop target (Favorites' reorder). */
  readonly drag?: {
    readonly onDragStart: DragEventHandler<HTMLElement>;
    readonly onDragEnter: DragEventHandler<HTMLElement>;
    readonly onDragOver: DragEventHandler<HTMLElement>;
    readonly onDrop: DragEventHandler<HTMLElement>;
    readonly onDragEnd: DragEventHandler<HTMLElement>;
    /** A drop here lands above this row: draw the line on its top edge. */
    readonly showDropLine: boolean;
  };
}

export function SidebarRow({ label, glyph, onOpen, trailing, drag }: SidebarRowProps) {
  const { showDropLine = false, ...dragHandlers } = drag ?? {};
  return (
    // `data-group`, not `role="group"`: it scopes the trailing control's
    // `_groupHover` without taking the row's listitem role from the list.
    <Flex
      as="li"
      data-group=""
      align="center"
      position="relative"
      borderRadius="md"
      _hover={{ bg: 'sidebar.item.hover' }}
      {...(drag && {
        draggable: true,
        ...dragHandlers,
        'data-drop-line': showDropLine || undefined,
      })}
    >
      {showDropLine && <TreeDropLine top={0} level={1} position="before" />}
      <Button
        variant="ghost"
        flex="1"
        minW={0}
        h="32px"
        px={2}
        justifyContent="flex-start"
        gap={2}
        fontSize="14px"
        fontWeight="normal"
        color="text.primary"
        bg="transparent"
        _hover={{ bg: 'transparent' }}
        onClick={onOpen}
      >
        <Glyph {...glyph} size="18px" gradient="warm" />
        <Text as="span" isTruncated title={label}>
          {label}
        </Text>
      </Button>
      {trailing}
    </Flex>
  );
}
